import type { DocumentReference } from '@google-cloud/firestore';
import {
  DestinationEntityRecordStatus,
  FailedDestinationEntityRecord,
  IDestinationEntity,
  IDestinationEntityJobRunOptions,
  IDestinationEntityRecord,
  IPracticeMigration,
  MergeConflictDestinationEntityRecord,
  SkippedDestinationEntityRecord,
  type IGetRecordResponse,
  type ISourceEntityRecord,
} from '@principle-theorem/practice-migrations/interfaces';
import { Media, Patient } from '@principle-theorem/principle-core';
import type {
  IMedia,
  IPatient,
} from '@principle-theorem/principle-core/interfaces';
import {
  FirestoreMigrate,
  getError,
  PrimaryKey,
  resolveSequentially,
  toTimestamp,
  WithRef,
} from '@principle-theorem/shared';
import { compact, groupBy, uniq } from 'lodash';
import { from, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { BaseDestinationEntity } from '../../../destination/base-destination-entity';
import { DestinationEntity } from '../../../destination/destination-entity';
import { CopyFiles, getBucketStoragePath, StorageFile } from '../../../storage';
import { TranslationMapHandler } from '../../../translation-map';
import {
  OasisPatient,
  PatientSourceEntity,
} from '../../source/entities/patients';
import {
  IPatientFileDestinationRecord,
  PATIENT_DOCUMENTS_RESOURCE_TYPE,
} from './patient-documents';
import { PatientDestinationEntity } from './patients';
import { PATIENT_RESOURCE_TYPE } from '../../../destination/entities/patient';

const IGNORE_FILE_TYPES = ['pvw', 'art', 'dat'];

export const PATIENT_VIXWIN_XRAYS_RESOURCE_TYPE = 'patientVixWinXrays';

export const PATIENT_VIXWIN_XRAYS_DESTINATION_ENTITY = DestinationEntity.init({
  metadata: {
    key: PATIENT_VIXWIN_XRAYS_RESOURCE_TYPE,
    label: 'Patient VixWin X-Rays',
    description: 'Patient VixWin X-Rays',
  },
});

interface IFileSummary {
  patientId: string;
  fileDate: string;
  fileName: string;
  folderPath: string;
}

interface IPatientDocumentJobData {
  sourcePatient: IGetRecordResponse<OasisPatient>;
  files: IFileSummary[];
}

export class PatientVixWinXrayDestinationEntity extends BaseDestinationEntity<
  IPatientFileDestinationRecord,
  IPatientDocumentJobData,
  IPatientDocumentJobData
> {
  override destinationEntity = PATIENT_VIXWIN_XRAYS_DESTINATION_ENTITY;
  sourceCountComparison = new PatientSourceEntity();

  override destinationEntities = {
    patients: new PatientDestinationEntity(),
  };

  override sourceEntities = {
    patients: new PatientSourceEntity(),
  };

  sourceCountDataAccessor(
    data: IPatientDocumentJobData
  ): PrimaryKey<ISourceEntityRecord> {
    return data.sourcePatient.record.id;
  }

  getDestinationEntityRecordUid(data: IPatientDocumentJobData): string {
    return data.sourcePatient.record.uid;
  }

  hasMergeConflict(
    _translationMap: TranslationMapHandler,
    _data: IPatientDocumentJobData
  ): IPatientDocumentJobData | undefined {
    return;
  }

  buildMergeConflictRecord(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    _translationMap: TranslationMapHandler,
    jobData: IPatientDocumentJobData,
    _migrationData: IPatientDocumentJobData
  ): IDestinationEntityRecord & MergeConflictDestinationEntityRecord {
    return {
      uid: jobData.sourcePatient.record.uid,
      label: jobData.sourcePatient.record.label,
      status: DestinationEntityRecordStatus.MergeConflict,
      sourceRef: jobData.sourcePatient.record.id,
    };
  }

  override buildJobData$(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    _translationMap: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<IPatientDocumentJobData[]> {
    const patientFiles$ = from(this._loadPatientFiles(migration));

    return patientFiles$.pipe(
      switchMap((patientFiles) => {
        return this.buildSourceRecordQuery$(
          migration,
          this.sourceEntities.patients,
          runOptions
        ).pipe(
          map((sourcePatients) =>
            sourcePatients.map((sourcePatient) => {
              const patientId = sourcePatient.data.id.toString();
              return {
                sourcePatient,
                files:
                  patientFiles[patientId.padStart(6, '0').toLowerCase()] || [],
              };
            })
          )
        );
      })
    );
  }

  buildMigrationData(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    _translationMap: TranslationMapHandler,
    data: IPatientDocumentJobData
  ):
    | IPatientDocumentJobData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord)
    | (IDestinationEntityRecord & SkippedDestinationEntityRecord) {
    return data;
  }

  async runJob(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMapHandler: TranslationMapHandler,
    jobData: IPatientDocumentJobData,
    migrationData: IPatientDocumentJobData
  ): Promise<IDestinationEntityRecord> {
    try {
      const documentRefs = await this.addDocumentsToPatient(
        migration,
        translationMapHandler,
        migrationData
      );

      if (!documentRefs.length) {
        return this.buildSkippedResponse(jobData);
      }

      return this._buildSuccessResponse(jobData.sourcePatient, documentRefs);
    } catch (error) {
      return this.buildErrorResponse(jobData.sourcePatient, getError(error));
    }
  }

  async addDocumentsToPatient(
    migration: WithRef<IPracticeMigration>,
    translationMap: TranslationMapHandler,
    migrationData: IPatientDocumentJobData
  ): Promise<DocumentReference<IMedia>[]> {
    const sourcePatientId = migrationData.sourcePatient.data.id.toString();
    if (!migrationData.files.length) {
      return [];
    }

    // eslint-disable-next-line no-console
    console.log(
      `Found ${migrationData.files.length} files for patient ${sourcePatientId} in the bucket`
    );

    const patientRef = await translationMap.getDestination<IPatient>(
      sourcePatientId,
      PATIENT_RESOURCE_TYPE
    );
    if (!patientRef) {
      throw new Error(`No ref for Patient Id ${sourcePatientId}`);
    }

    const copyStorage = this.getCopyStorage(
      getBucketStoragePath(migration.source),
      patientRef,
      migration.configuration.projectId,
      migration.configuration.destinationBucket
    );

    return resolveSequentially(migrationData.files, async (document) => {
      const filePath = await copyStorage.copy(document.folderPath);
      if (!filePath) {
        throw new Error(`Failed to copy file: ${document.folderPath}`);
      }

      const fileDestinationRef = await translationMap.getDestination(
        document.folderPath,
        PATIENT_DOCUMENTS_RESOURCE_TYPE
      );

      const mediaRef = await FirestoreMigrate.upsertDoc(
        Patient.mediaCol({ ref: patientRef }),
        {
          ...Media.init({
            name: document.fileName,
            path: filePath,
          }),
          createdAt: toTimestamp(),
          deleted: false,
        },
        fileDestinationRef?.id
      );

      if (!fileDestinationRef) {
        await translationMap.upsert({
          sourceIdentifier: document.folderPath,
          destinationIdentifier: mediaRef,
          resourceType: PATIENT_DOCUMENTS_RESOURCE_TYPE,
        });
      }

      return mediaRef;
    });
  }

  getCopyStorage(
    sourceBucket: string,
    patientRef: DocumentReference<IPatient>,
    projectId: string,
    destinationBucket: string
  ): StorageFile {
    return new StorageFile(
      sourceBucket,
      destinationBucket,
      Patient.storagePath({ ref: patientRef }),
      projectId
    );
  }

  buildErrorResponse(
    sourcePatient: IGetRecordResponse<OasisPatient>,
    errorMessage: string
  ): IDestinationEntityRecord & FailedDestinationEntityRecord {
    return {
      uid: sourcePatient.record.uid,
      label: sourcePatient.record.label,
      status: DestinationEntityRecordStatus.Failed,
      sourceRef: sourcePatient.record.id,
      errorMessage,
      failData: {
        patientRef: sourcePatient.record.id,
      },
    };
  }

  buildSkippedResponse(
    data: IPatientDocumentJobData
  ): IDestinationEntityRecord & SkippedDestinationEntityRecord {
    return {
      uid: data.sourcePatient.record.uid,
      label: data.sourcePatient.record.label,
      status: DestinationEntityRecordStatus.Skipped,
      sourceRef: data.sourcePatient.record.id,
    };
  }

  private async _loadPatientFiles(
    migration: WithRef<IPracticeMigration>
  ): Promise<Record<string, IFileSummary[]>> {
    const projectId = migration.configuration.projectId;
    const bucketName = getBucketStoragePath(migration.source);
    const bucketPath = 'vixwin';

    const storage = new CopyFiles(bucketName, bucketPath, '', '', projectId)
      .storage;

    const [files] = await storage.bucket(bucketName).getFiles({
      prefix: bucketPath,
    });

    const fileSummaries = uniq(
      compact(
        files.map((file) => {
          const pathParts = file.name.split('/');
          if (pathParts.length !== 2) {
            // Ignore files in subfolders like vixwin/RECYCLE/ or vixwin/VXIMAGES/RECYCLE/
            return false;
          }

          const baseName = file.name.split('/').pop() ?? file.name;
          const fileName = baseName.toLowerCase();
          const vixWinPatientId = fileName.substring(0, 6);

          const extension = file.name.split('.').pop();
          if (
            !extension ||
            IGNORE_FILE_TYPES.includes(extension.toLowerCase())
          ) {
            return false;
          }

          return {
            patientId: vixWinPatientId,
            fileDate: '',
            fileName: baseName,
            folderPath: file.name,
          };
        })
      )
    );

    return groupBy(fileSummaries, 'patientId');
  }

  private _buildSuccessResponse(
    sourcePatient: IGetRecordResponse<OasisPatient>,
    fileRefs: DocumentReference<IMedia>[]
  ): IDestinationEntityRecord<IPatientFileDestinationRecord> {
    return {
      uid: sourcePatient.record.uid,
      label: sourcePatient.record.label,
      data: {
        fileRefs,
      },
      status: DestinationEntityRecordStatus.Migrated,
      sourceRef: sourcePatient.record.id,
      migratedAt: toTimestamp(),
    };
  }
}
